import type { ApolloLink } from '@apollo/client';
import sha256 from 'crypto-js/sha256.js';
import { uniq } from 'lodash-es';
import qs from 'qs';

import type { ErrorReporter } from '@change-corgi/core/errorReporter/common';

import type { WebappInfo } from '../webappInfoLink';

import { getRequestGqlDetails } from './getRequestGqlDetails';
import { getRequestMethod } from './getRequestMethod';
import { getRequestUrl } from './getRequestUrl';
import { reportResponseError } from './reportResponseError';

function withGraphqlHashHeader(
	init: RequestInit | undefined,
	webappInfo: WebappInfo | undefined,
): RequestInit | undefined {
	if (!webappInfo?.version || !init?.body || typeof init.body !== 'string') {
		return init;
	}
	const initialHeaders = init.headers;
	if (initialHeaders instanceof Headers || Array.isArray(initialHeaders)) {
		// Headers instance not yet supported for server-side fetch
		// Array of headers not yet supported for server-side fetch
		return init;
	}
	const hash = sha256(`${webappInfo.version}:${init.body}`).toString().slice(0, 32);
	// eslint-disable-next-line @typescript-eslint/naming-convention
	const headers = { ...initialHeaders, 'X-Change-Graphql-Hash': hash };
	return { ...init, headers };
}

type Options = {
	fetch: typeof fetch;
	errorReporter: ErrorReporter;
	networkErrorReporter: ErrorReporter;
	addOperationNamesToUri?: boolean;
	webappInfo?: WebappInfo;
};

// No idea why operationName is not in ApolloLink.Request
type GraphQLRequest = ApolloLink.Request & {
	operationName: string;
};

function getOperationNames(bodyJson: BodyInit | undefined | null): readonly string[] {
	if (typeof bodyJson !== 'string') return [];
	try {
		const body = JSON.parse(bodyJson) as GraphQLRequest | GraphQLRequest[];
		const bodyArray = Array.isArray(body) ? body : [body];
		return bodyArray
			.map(({ operationName }) => operationName)
			.filter((operationName): operationName is string => !!operationName);
	} catch (e) {
		return [];
	}
}

function buildUrl(uri: RequestInfo | URL, operationNames: readonly string[] | undefined) {
	if (typeof uri !== 'string' || !operationNames || !operationNames.length) return uri;
	const [url, query] = uri.split('?');
	return `${url}?${qs.stringify(
		{
			...qs.parse(query),
			op: uniq(operationNames)
				.sort()
				.map((operationName) => {
					const count = operationNames.filter((name) => name === operationName).length;
					return count > 1 ? `${operationName}~${count}` : operationName;
				}),
		},
		{ arrayFormat: 'repeat' },
	)}`;
}

export function wrapFetch({
	fetch: originalFetch,
	errorReporter,
	networkErrorReporter,
	addOperationNamesToUri,
	webappInfo,
}: Options): typeof fetch {
	// Patch fetch to ensure error includes relevant debugging info

	return async function fetch(infoParam, init) {
		/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument */
		const info = addOperationNamesToUri ? buildUrl(infoParam as any, getOperationNames(init?.body)) : infoParam;

		try {
			const result = await originalFetch(info as any, withGraphqlHashHeader(init, webappInfo));
			if (!result.ok) {
				// eslint-disable-next-line @typescript-eslint/only-throw-error
				throw result;
			}
			return result;
		} catch (e) {
			if ((e as Response).status) {
				const response = e as Response;

				reportResponseError(init, info as any, response, errorReporter);

				if (response.status < 500) {
					return response;
				}
				throw e;
			}
			void networkErrorReporter.report({
				error: e as Error,
				params: {
					url: getRequestUrl(info as any),
					method: getRequestMethod(info as any, init),
					...getRequestGqlDetails(init),
				},
			});
			throw e;
		}
		/* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument */
	};
}
